/** Shared, dependency-free contract bundled directly by the standalone widget. */
import { isSystemVariable } from "./dynamic-variables";
export interface EmbedErrorDetails {
  missingKeys?: string[];
  invalidKeys?: string[];
  unauthorizedKeys?: string[];
  tooLongKeys?: string[];
  status?: number;
  reason?: string;
  retryAfterSeconds?: number;
}

export class EmbedApiError extends Error {
  readonly code: string;
  readonly missingKeys: string[];
  readonly invalidKeys: string[];
  readonly unauthorizedKeys: string[];
  readonly tooLongKeys: string[];
  readonly status?: number;
  readonly reason?: string;
  readonly retryAfterSeconds?: number;

  constructor(code: string, details: EmbedErrorDetails = {}) {
    super(code);
    this.name = "EmbedApiError";
    this.code = code;
    this.missingKeys = details.missingKeys ?? [];
    this.invalidKeys = details.invalidKeys ?? [];
    this.unauthorizedKeys = details.unauthorizedKeys ?? [];
    this.tooLongKeys = details.tooLongKeys ?? [];
    this.status = details.status;
    this.reason = details.reason;
    this.retryAfterSeconds = details.retryAfterSeconds;
  }
}

export const EMBED_VARIABLE_MAX_LENGTH = 500;

export function assertEmbedVariableRecord(
  input: unknown,
): asserts input is Record<string, unknown> {
  if (!input || typeof input !== "object" || Array.isArray(input)) {
    throw new EmbedApiError("invalid_dynamic_variables");
  }
}

export function normalizeEmbedVariable(value: unknown): string | null {
  if (
    typeof value !== "string" &&
    typeof value !== "boolean" &&
    !(typeof value === "number" && Number.isFinite(value))
  )
    return null;
  return String(value).replace(/[\x00-\x1F\x7F]/g, " ");
}

/** All issues carry keys only, never visitor values. Required is distinct from allowed. */
export function validateEmbedVariables(
  input: unknown,
  allowedKeys: readonly string[],
  requiredKeys: readonly string[],
): Record<string, string> {
  assertEmbedVariableRecord(input);
  const allowed = new Set(allowedKeys);
  const unauthorizedKeys = [
    ...new Set([
      ...Object.keys(input).filter(
        (key) => !allowed.has(key) || isSystemVariable(key),
      ),
      ...requiredKeys.filter((key) => !allowed.has(key)),
    ]),
  ];
  const invalidKeys: string[] = [];
  const tooLongKeys: string[] = [];
  const normalized = new Map<string, string>();
  for (const [key, raw] of Object.entries(input)) {
    const value = normalizeEmbedVariable(raw);
    if (value === null) invalidKeys.push(key);
    else {
      normalized.set(key, value);
      if (value.length > EMBED_VARIABLE_MAX_LENGTH) tooLongKeys.push(key);
    }
  }
  const missingKeys = requiredKeys.filter(
    (key) => !normalized.get(key)?.trim(),
  );
  if (
    unauthorizedKeys.length ||
    invalidKeys.length ||
    tooLongKeys.length ||
    missingKeys.length
  ) {
    const code =
      invalidKeys.length || tooLongKeys.length
        ? "invalid_dynamic_variables"
        : unauthorizedKeys.length
          ? "unauthorized_dynamic_variables"
          : "missing_dynamic_variables";
    throw new EmbedApiError(code, {
      missingKeys,
      invalidKeys,
      unauthorizedKeys,
      tooLongKeys,
    });
  }
  return Object.fromEntries(normalized);
}
